import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import { Loader, Typography, textStyleMixin } from '@elfsight/widget-ui';
import { isMobile } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { FileEmbedsFile } from '../../types';
import { Icon } from '../common';
import {
  getClassName as defaultGetClassName,
  getLinkAriaLabel
} from '../helpers';

const getClassName = (component: string) =>
  defaultGetClassName('embed-file', component);

interface EmbedFileProps {
  item: FileEmbedsFile;
}

export const EmbedFile = ({ item }: EmbedFileProps) => {
  const iframe = useRef<HTMLIFrameElement>(null);
  const fileWrapper = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    width,
    heightFactor,
    fileBorderSize,
    fileBorderColor,
    showDownloadLink,
    downloadLinkText,
    nameTextStyle,
    nameColor,
    showIcon,
    downloadTextStyle,
    downloadColor,
    fileBackgroundColor,
    previewImageSize,
    icon
  } = useSettings();

  const { name, link, previewImage, altText } = item;

  const { url, extension } = link ?? {};

  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const [height, setHeight] = useState(
    Number.parseInt(width as string, 10) * heightFactor
  );
  const [currentWidth, setWidth] = useState(0);
  const isMobileDevice = isMobile();

  const extensions = useMemo(
    () => ({
      xls: {
        mask: /xlsx|xls/,
        link: `https://view.officeapps.live.com/op/embed.aspx?src=${url}`
      },
      pp: {
        mask: /pptx|ppt|pps/,
        link: `https://view.officeapps.live.com/op/embed.aspx?src=${url}`
      },
      doc: {
        mask: /docx|doc|txt|rtf/,
        link: `https://view.officeapps.live.com/op/embed.aspx?src=${url}`
      },
      pdf: {
        mask: /pdf/,
        link: isMobileDevice
          ? `https://docs.google.com/viewerng/viewer?url=${url}&embedded=true`
          : `${url}#navpanes=0`
      },
      audio: {
        mask: /wav|mp3/,
        link: url
      },
      img: {
        mask: /jpeg|jpg|png|gif|svg/,
        link: url
      }
    }),
    [url, isMobileDevice]
  );

  const file = useMemo(() => {
    const fileType = Object.keys(extensions).find(
      (key) => extension && extensions[key]?.mask.test(extension)
    );

    if (!fileType) {
      return;
    }

    return {
      type: fileType,
      link: extensions[fileType]?.link
    };
  }, [extension, extensions]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);

    if (timer) {
      clearInterval(timer);
    }
  }, [timer]);

  useEffect(() => {
    if (!fileWrapper.current || !iframe.current || !isMobileDevice) {
      return;
    }

    const reloadIFrame = () => {
      if (iframe?.current?.src) {
        const currentSrc = iframe.current.src;
        iframe.current.src = currentSrc;
      }
    };

    const checkIfLoaded = () => {
      try {
        if (!iframe?.current?.contentWindow?.document.body.innerHTML) {
          reloadIFrame();
        }
      } catch (_error) {
        handleLoad();
      }
    };

    if (!timer) {
      setTimer(setInterval(checkIfLoaded, 5000));
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [file, timer, isMobileDevice, handleLoad]);

  useEffect(() => {
    if (!fileWrapper.current) {
      return;
    }

    requestAnimationFrame(() => {
      const newWidth = fileWrapper.current?.clientWidth ?? 0;

      setWidth(newWidth);
      setHeight(newWidth * heightFactor);
    });
  });

  if (file?.type === 'img' && isLoading) {
    setIsLoading(false);
  }

  useEffect(() => {
    setIsLoading(true);
  }, [file?.link]);

  const iconName =
    file?.type === 'img' || !file?.type ? 'default' : (icon ?? file.type);

  return (
    <Component
      tabIndex={0}
      _fileBackgroundColor={fileBackgroundColor}
      _fileBorderSize={fileBorderSize}
      _fileBorderColor={fileBorderColor}
    >
      <Header>
        {(showIcon || previewImage) && (
          <StyledIcon
            iconClassName={getClassName('icon')}
            name={iconName}
            text={extension}
            size={{
              w: previewImageSize,
              h: previewImageSize
            }}
            previewImage={previewImage}
            altText={altText ?? ''}
          />
        )}

        <HeaderWrapper _currentWidth={currentWidth}>
          <Name textStyle={nameTextStyle} color={nameColor}>
            {name}

            {!url && <Error>No specified file</Error>}
          </Name>

          {showDownloadLink && (
            <LinkWrapper _currentWidth={currentWidth}>
              <Link
                aria-label={getLinkAriaLabel(link)}
                as="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                download
                _color={downloadColor}
                _textStyle={downloadTextStyle}
              >
                {downloadLinkText}
              </Link>
            </LinkWrapper>
          )}
        </HeaderWrapper>
      </Header>

      <Wrapper _type={file?.type} _height={height} ref={fileWrapper}>
        {file && file.type !== 'img' && (
          <IFrame
            key={file.link}
            ref={iframe}
            title="doc"
            src={file.link}
            onLoad={handleLoad}
          />
        )}

        {file?.type === 'img' && (
          <ImageContainer>
            <Image src={file.link} alt={name} />
          </ImageContainer>
        )}

        {isLoading && <StyledLoader size={20} />}
      </Wrapper>
    </Component>
  );
};

const Component = styled.div.attrs<{
  _fileBackgroundColor: string;
  _fileBorderSize?: number;
  _fileBorderColor?: string;
}>({
  className: getClassName('component')
})`
  overflow: hidden;
  box-sizing: border-box;

  ${({ _fileBackgroundColor }) =>
    _fileBackgroundColor && `background-color: ${_fileBackgroundColor};`}
  ${({ _fileBackgroundColor }) =>
    _fileBackgroundColor && _fileBackgroundColor !== 'transparent'
      ? 'padding: 20px;'
      : ''}
    ${({ _fileBorderSize, _fileBorderColor }) =>
    _fileBorderSize && _fileBorderColor
      ? `border: ${_fileBorderSize}px solid ${_fileBorderColor};`
      : ''}

    &:not(:last-child) {
    margin-bottom: 40px;
  }
`;

const Header = styled.div.attrs({
  className: getClassName('header')
})`
  display: flex;
  padding-bottom: 16px;
  align-items: center;
`;

const StyledIcon = styled(Icon).attrs({
  className: getClassName('iconContainer')
})`
  margin-right: 16px;
`;

const HeaderWrapper = styled.div.attrs<{
  _currentWidth: number;
}>({
  className: getClassName('headerWrapper')
})`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  ${({ _currentWidth }) => _currentWidth <= 260 && 'flex-direction: column;'}
`;

const Name = styled(Typography).attrs({
  className: getClassName('name')
})``;

const Error = styled.div.attrs({
  className: getClassName('error')
})`
  font-size: 11px;
  line-height: 14px;
  color: rgb(222, 43, 51);
`;

const Link = styled.a.attrs<{
  _color: string;
  _textStyle: TextStyleValue;
}>({
  className: getClassName('link')
})`
  text-decoration: none;
  color: ${({ _color }) => _color};

  ${({ _textStyle }) => textStyleMixin({ _textStyle })}

  &:hover {
    text-decoration: underline;
  }
`;

const LinkWrapper = styled.div.attrs<{
  _currentWidth: number;
}>({
  className: getClassName('linkWrapper')
})`
  margin-left: auto;
  padding-left: 12px;
  ${({ _currentWidth }) =>
    _currentWidth <= 260 &&
    `
    padding-left: 0;
    margin-left: 0;
  `}
`;

const Wrapper = styled.div.attrs<{
  _type?: string;
  _height: number;
}>({
  className: getClassName('wrapper')
})`
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  background: #d1d1d1;
  position: relative;

  ${({ _type, _height }) => {
    if (_type === 'img') {
      return `
        background: transparent;
        justify-content: flex-start;
      `;
    }

    return `height: ${_height}px;`;
  }}
`;

const StyledLoader = styled(Loader).attrs({
  className: getClassName('spinner')
})`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const IFrame = styled.iframe.attrs({
  className: getClassName('iframe')
})`
  /* Нужно чтоб убрать рамку, которую ставит Microsoft Office */
  width: calc(100% + 2px);
  height: calc(100% + 2px);
  margin: 0 -1px;
  border: 0;
`;

const ImageContainer = styled.div.attrs({
  className: getClassName('imageContainer')
})`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  border-radius: 4px;
  overflow: hidden;
`;

const Image = styled.img.attrs({
  className: getClassName('image')
})`
  max-width: 100%;
`;
